import uPlot from 'uplot';

import {type SeriesEntry} from './getSeriesEntries';

const MARKER_RADIUS = 3; // radius in CSS pixels
const MARKER_STROKE_WIDTH = 1;
const MARKER_STROKE_WIDTH_HOVERED = 2;
const HOVER_PROXIMITY_THRESHOLD = 50; // CSS pixels

export type NonFiniteMarker = {
  x: number;
  y: number;
  color: string;
  lineName: string | undefined;
  metricName: string | undefined;
  /** uPlot series index of the series this marker belongs to */
  seriesIdx: number;
};

/**
 * State that can be updated externally and read by the draw hook.
 */
export type NonFiniteMarkerState = {
  /** Index of the currently hovered marker, or -1 if none */
  hoveredMarkerIndex: number;
  /** dimFactor if the plot is currently dimmed, or undefined if not */
  dimFactor: number | undefined;
};

export function getNonFiniteMarkers(
  seriesEntries: (SeriesEntry | null)[]
): NonFiniteMarker[] {
  const result: NonFiniteMarker[] = [];

  for (let seriesIdx = 0; seriesIdx < seriesEntries.length; seriesIdx++) {
    const entry = seriesEntries[seriesIdx];
    if (entry == null) {
      continue;
    }
    // A band source line appears twice (band-min and band-max share the same
    // line); emit its markers once, keyed to the band-min series.
    if (entry.type === 'band-max') {
      continue;
    }
    const line = entry.line;
    if (!line.nanPoints || line.nanPoints.length === 0) {
      continue;
    }

    for (const point of line.nanPoints) {
      result.push({
        x: point.x,
        y: point.y,
        color: line.color ?? 'red',
        lineName: line.name,
        metricName: line.metricName,
        seriesIdx,
      });
    }
  }

  return result;
}

/**
 * Finds the index of the marker closest to the cursor position.
 * Returns -1 if no marker is within the proximity threshold.
 */
export function findHoveredMarkerIndex(
  u: uPlot,
  markers: NonFiniteMarker[],
  cursorLeft: number,
  cursorTop: number
): number {
  if (markers.length === 0) {
    return -1;
  }

  let closestIndex = -1;
  let closestDist = Infinity;

  for (let i = 0; i < markers.length; i++) {
    const marker = markers[i];

    // Convert marker to CSS pixel coordinates
    const px = u.valToPos(marker.x, 'x');
    const py = u.valToPos(marker.y, 'y');

    if (!Number.isFinite(px) || !Number.isFinite(py)) {
      continue;
    }

    // Calculate distance from cursor
    const dx = px - cursorLeft;
    const dy = py - cursorTop;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dist < closestDist && dist < HOVER_PROXIMITY_THRESHOLD) {
      closestDist = dist;
      closestIndex = i;
    }
  }

  return closestIndex;
}

/**
 * Creates a uPlot draw hook that renders hollow circle markers for non-finite values
 * (Infinity, -Infinity, NaN) directly on the canvas.
 *
 * These markers are positioned at the clamped y-coordinates (top of chart for Infinity,
 * bottom for -Infinity and NaN) but the tooltip will show the original non-finite value.
 */
export function createDrawNonFiniteMarkersHook(
  markers: NonFiniteMarker[],
  stateRef: React.RefObject<NonFiniteMarkerState>
): (u: uPlot) => void {
  return (u: uPlot) => {
    if (markers.length === 0) {
      return;
    }

    const ctx = u.ctx;
    const xScale = u.scales.x;
    const yScale = u.scales.y;

    if (!xScale || !yScale) {
      return;
    }

    const state = stateRef.current;
    if (!state) {
      return;
    }
    const {hoveredMarkerIndex, dimFactor} = state;

    const pxRatio = uPlot.pxRatio;

    ctx.save();

    for (let i = 0; i < markers.length; i++) {
      const marker = markers[i];

      // Markers follow their series' visibility. The highlight overlay hides
      // non-highlighted series via setSeries({show: false}); their markers
      // must hide with them or they render undimmed over the base layer.
      if (u.series[marker.seriesIdx]?.show === false) {
        continue;
      }

      // Check if point is within visible x range
      if (
        xScale.min != null &&
        xScale.max != null &&
        (marker.x < xScale.min || marker.x > xScale.max)
      ) {
        continue;
      }

      const cx = u.valToPos(marker.x, 'x', true);
      const cy = u.valToPos(marker.y, 'y', true);

      if (!Number.isFinite(cx) || !Number.isFinite(cy)) {
        continue;
      }

      const isHovered = i === hoveredMarkerIndex;

      // Use thicker stroke for hovered marker
      const strokeWidth = isHovered
        ? MARKER_STROKE_WIDTH_HOVERED
        : MARKER_STROKE_WIDTH;

      // Draw hollow circle (transparent fill, colored stroke)
      ctx.beginPath();
      ctx.arc(cx, cy, MARKER_RADIUS * pxRatio, 0, 2 * Math.PI);
      ctx.strokeStyle = marker.color;
      ctx.lineWidth = strokeWidth * pxRatio;
      ctx.globalAlpha = dimFactor ?? 1;
      ctx.stroke();
    }

    ctx.restore();
  };
}
